import axios from 'axios';
import { _defaults } from 'underscore-es';
import queryString from 'query-string';

import { BiMap } from '../../../libs/data-structures';
import store from '../../../redux/store';
import { userDetailSelector } from '../../../redux/selectors/user';
import { selectedOrganizationSelector } from '../../../redux/selectors/organization';
import UnexpectedError from './UnexpectedError';
import InvalidDataError from './InvalidDataError';
import NotFoundError from './NotFoundError';
import generateObjectId from '../utils/generateObjectId';

const getUserId = () => {
  return userDetailSelector(store.getState()).id;
};

const getOrganizationId = () => {
  return selectedOrganizationSelector(store.getState()).id;
};

// Helper function to validate headers
const validateHeaders = () => {
  const userId = getUserId();
  const organizationId = getOrganizationId();
  const missingHeaders = [];
  if (!userId) missingHeaders.push('userid');
  if (!organizationId) missingHeaders.push('organizationid');

  if (missingHeaders.length > 0) {
    throw new InvalidDataError([
      `${missingHeaders.join(' and ')} should not be empty`,
    ]);
  }

  return { userId, organizationId };
};

// Helper function to parse string to object
const parseStringToObject = (str, type = 'object') => {
  if (typeof str === 'string') {
    try {
      return JSON.parse(str);
    } catch (e) {
      console.error(`Failed to parse ${type} string:`, str, e);
      return null;
    }
  }
  return str;
};

// Helper function to validate object
const isValidObject = (obj, type = 'object') => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    console.error(`Invalid ${type} object:`, obj);
    return false;
  }
  return true;
};

// Helper function to validate template
const isValidTemplate = template => {
  return (
    template !== undefined &&
    template !== null &&
    typeof template === 'object' &&
    template.name &&
    typeof template.name === 'string' &&
    template.name.trim().length > 0 &&
    template.html &&
    typeof template.html === 'string' &&
    template.html.trim().length > 0
  );
};

// Helper function to handle API errors
const handleApiError = (error, options = {}) => {
  const {
    returnNullOn404 = false,
    resourceName = null,
    resourceId = null,
  } = options;

  const response = error.response;

  // If it's already an InvalidDataError, re-throw it
  if (error instanceof InvalidDataError) {
    throw error;
  }

  if (!response) {
    // If it's already an InvalidDataError (from interceptor), re-throw it
    if (error instanceof InvalidDataError) {
      throw error;
    }
    // Network error or other non-response error
    throw new UnexpectedError();
  }

  // Handle 404 Not Found
  if (response.status === 404) {
    if (returnNullOn404) {
      return null;
    }
    if (resourceName && resourceId) {
      throw new NotFoundError(resourceName, resourceId);
    }
    throw new NotFoundError('resource', null);
  }

  // Handle 400 Bad Request
  if (response.status === 400) {
    const errorMessage = response.data?.message;
    // Ensure errorMessage is an array for InvalidDataError
    const validationErrors = Array.isArray(errorMessage)
      ? errorMessage
      : errorMessage
      ? [errorMessage]
      : ['Validation failed'];

    // Log detailed validation errors for debugging
    console.error('Validation errors:', validationErrors);

    throw new InvalidDataError(validationErrors);
  }

  throw new UnexpectedError();
};

const http = axios.create({
  baseURL: process.env.REACT_APP_AUTOMATED_CAMPAIGNS_BASE_URL,
  paramsSerializer: params =>
    queryString.stringify(params, { arrayFormat: 'comma' }),
});

const httpPublic = axios.create({
  baseURL: process.env.REACT_APP_AUTOMATED_CAMPAIGNS_BASE_URL,
});

http.interceptors.request.use(request => {
  // Validate headers and set them - throws InvalidDataError if missing
  const { userId, organizationId } = validateHeaders();

  request.headers.userid = userId;
  request.headers.organizationid = organizationId;
  return request;
});

const ApiUnit = {
  DAY: 'd',
  WEEK: 'w',
  MONTH: 'm',
  YEAR: 'y',
};

export const Unit = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
};

export const Order = {
  BEFORE: 'before',
  AFTER: 'after',
};

const MULTIPLIER_BY_ORDER = {
  [Order.BEFORE]: -1,
  [Order.AFTER]: 1,
};

const API_UNIT_TO_UNIT = new BiMap({
  [ApiUnit.DAY]: Unit.DAY,
  [ApiUnit.WEEK]: Unit.WEEK,
  [ApiUnit.MONTH]: Unit.MONTH,
  [ApiUnit.YEAR]: Unit.YEAR,
});

const transformDelayFromApi = rawDelay => {
  if (typeof rawDelay === 'number') {
    rawDelay = `${rawDelay}|d`;
  }

  const [rawValue, rawUnit = ApiUnit.DAY] = rawDelay.split('|');
  const orderedValue = Number(rawValue);
  const value = Math.abs(orderedValue);
  const order = orderedValue < 0 ? Order.BEFORE : Order.AFTER;
  const unit = API_UNIT_TO_UNIT.get(rawUnit);
  return { value, order, unit };
};

const transformDelayToApi = delay => {
  if (!delay || typeof delay !== 'object') {
    console.error('Invalid delay object:', delay);
    // Return default delay if invalid
    return `0|${ApiUnit.DAY}`;
  }

  const value = delay.value ?? 0;
  const order = delay.order ?? Order.AFTER;
  const unit = delay.unit ?? Unit.DAY;

  if (!MULTIPLIER_BY_ORDER[order]) {
    console.error('Invalid delay order:', order);
    return `0|${ApiUnit.DAY}`;
  }

  if (!API_UNIT_TO_UNIT.get(unit)) {
    console.error('Invalid delay unit:', unit);
    return `0|${ApiUnit.DAY}`;
  }

  const orderedValue = value * MULTIPLIER_BY_ORDER[order];
  return `${orderedValue}|${API_UNIT_TO_UNIT.get(unit)}`;
};

// Helper function to transform condition to API format
// Returns minimal condition object even for incomplete data to allow saving
const transformConditionToApi = condition => {
  // Ensure condition is an object, not a string
  let conditionObj = parseStringToObject(condition, 'condition');

  // Return minimal condition even if incomplete - allow saving partial data
  if (!conditionObj || !isValidObject(conditionObj, 'condition')) {
    return {
      _id: conditionObj?._id || null,
      name: conditionObj?.name || null,
      delay: conditionObj?.delay || `0|${ApiUnit.DAY}`,
    };
  }

  // Create a clean condition object
  const transformedCondition = {
    _id: conditionObj._id,
    name: conditionObj.name,
  };

  // Transform delay if it exists
  if (conditionObj.delay !== undefined && conditionObj.delay !== null) {
    try {
      transformedCondition.delay =
        typeof conditionObj.delay === 'object'
          ? transformDelayToApi(conditionObj.delay)
          : conditionObj.delay;
    } catch (error) {
      console.error('Error transforming delay:', error, conditionObj.delay);
      // Use default delay if transformation fails
      transformedCondition.delay = `0|${ApiUnit.DAY}`;
    }
  }

  // Include option if it exists
  if (conditionObj.option !== undefined) {
    transformedCondition.option = conditionObj.option;
  }

  // Include date if it exists (for custom date conditions)
  if (conditionObj.date !== undefined) {
    transformedCondition.date = conditionObj.date;
  }

  return transformedCondition;
};

const transformCampaignFromApi = campaign => {
  const stages = Array.isArray(campaign.stages) ? campaign.stages : [];
  return {
    ...campaign,
    stages: stages.map(stage => ({
      ...stage,
      analytics: _defaults(stage.analytics ?? {}, {
        sent: 0,
        opened: 0,
        clicked: 0,
      }),
      conditions: (stage.conditions && Array.isArray(stage.conditions)
        ? stage.conditions
        : []
      ).map(condition => {
        return {
          ...condition,
          delay: transformDelayFromApi(condition.delay),
        };
      }),
    })),
    createdAt: campaign.createdAt ? new Date(campaign.createdAt) : null,
    updatedAt: campaign.updatedAt ? new Date(campaign.updatedAt) : null,
  };
};

const transformCampaignToApi = campaign => {
  // Only include top-level fields that are present
  const transformedCampaign = {};
  if (campaign.name) transformedCampaign.name = campaign.name;
  if (campaign.description)
    transformedCampaign.description = campaign.description;
  if (campaign.status) transformedCampaign.status = campaign.status;
  if (campaign.audience) transformedCampaign.audience = campaign.audience;

  // Always include stages when present, even if empty or incomplete
  if (campaign.stages !== undefined) {
    if (Array.isArray(campaign.stages)) {
      // Include stages array even if empty - allow saving campaigns with no stages yet
      transformedCampaign.stages = campaign.stages.map(stage => {
        // Ensure stage is an object, not a string
        const stageObj = parseStringToObject(stage, 'stage');

        // Include stage even if incomplete - allow saving partial data
        if (!stageObj || !isValidObject(stageObj, 'stage')) {
          // Return minimal stage object to preserve incomplete data
          return {
            _id: stageObj?._id,
            name: stageObj?.name || null,
            description: stageObj?.description || null,
          };
        }

        // Create a clean stage object with only the fields we need
        const transformedStage = {
          _id: stageObj._id,
          name: stageObj.name,
          description: stageObj.description,
        };

        // Transform conditions if they exist - include even if empty or incomplete
        if (stageObj.conditions && Array.isArray(stageObj.conditions)) {
          transformedStage.conditions = stageObj.conditions.map(condition =>
            transformConditionToApi(condition)
          );
        }

        // Include template even if incomplete - allow saving partial templates
        if (stageObj.template !== undefined && stageObj.template !== null) {
          // Include template regardless of validity - backend can handle validation
          transformedStage.template = stageObj.template;
        }

        return transformedStage;
      });
    } else {
      // If stages is not an array, include empty array to allow saving
      transformedCampaign.stages = [];
    }
  }

  return transformedCampaign;
};

const ESCAPE_CHARACTER = '\\';
const escapeCharacter = (text, character) => {
  const characterRegexp = new RegExp(`\\${character}`, 'g');
  const escapedCharacter = ESCAPE_CHARACTER + character;
  return text.replace(characterRegexp, escapedCharacter);
};

const escapeCharacters = (text, characters) => {
  const withEscapeCharacterEscaped = escapeCharacter(text, ESCAPE_CHARACTER);
  return characters.reduce(escapeCharacter, withEscapeCharacterEscaped);
};

const FILTER_SPECIAL_CHARACTERS = ['|', '(', ')', ','];
const escapeFilterSegment = segment => {
  return escapeCharacters(segment, FILTER_SPECIAL_CHARACTERS);
};

const generateFilter = ({ field, value, operator }) => {
  const escapedField = escapeFilterSegment(field);
  const escapedValue = escapeFilterSegment(value);
  const escapedOperator = escapeFilterSegment(operator);
  return `(${escapedField}|${escapedValue}|${escapedOperator})`;
};

const generateFilters = filters => {
  const queries = filters.map(generateFilter);
  return queries.join(',');
};

export const fetchCampaigns = async ({
  page,
  pageSize,
  sortBy,
  sortDirection,
  search,
  statuses,
}) => {
  const params = {
    page,
    size: pageSize,
    sortBy: sortBy,
    sortDirection: sortDirection,
    filter: search
      ? generateFilters([
          { field: 'name', value: search, operator: 'contains' },
          { field: 'description', value: search, operator: 'contains' },
          { field: 'status', value: search, operator: 'contains' },
        ])
      : null,
    filterLogic: 'or',
    statuses,
  };
  const { data } = await http.get('/campaigns', { params });
  const campaigns = data.data.map(transformCampaignFromApi);

  return {
    total: data.meta.pagination.itemsCount,
    campaigns,
  };
};

export const fetchCampaign = async id => {
  try {
    const { data } = await http.get(`/campaigns/${id}`);
    return transformCampaignFromApi(data.data);
  } catch (error) {
    handleApiError(error, { resourceName: 'campaign', resourceId: id });
  }
};

export const updateCampaign = async (id, updates) => {
  try {
    const transformedUpdates = transformCampaignToApi(updates);
    await http.patch(`/campaigns/${id}`, transformedUpdates);
  } catch (error) {
    handleApiError(error);
  }
};

export const createCampaign = async newCampaignData => {
  try {
    const response = await http.post(
      '/campaigns',
      transformCampaignToApi(newCampaignData)
    );
    // Return the created campaign data (including _id) so we can use it for autosave
    return transformCampaignFromApi(response.data.data);
  } catch (error) {
    handleApiError(error);
  }
};

export const changeStatusOfSelectedCampaigns = async ({ ids, status }) => {
  try {
    await http.post('/campaigns/changeStatus', { ids, status });
  } catch (error) {
    throw new UnexpectedError();
  }
};

export const fetchEngagement = async contactId => {
  try {
    const { data } = await http.get(`/events/engagement/${contactId}`);
    return data?.data;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const fetchRecentEngagement = async (contactId, limit) => {
  try {
    const { data } = await http.get(`/events/engagement/${contactId}`, {
      params: { limit },
    });
    return data?.data;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const fetchACEvent = async sgCompleteId => {
  try {
    const { data } = await http.get(
      `/events/engagement/events/${sgCompleteId}`
    );
    return data?.data;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const fetchACMessage = async (acID, stageID) => {
  try {
    const { data } = await httpPublic.get(
      `/events/engagement/message/${acID}/${stageID}`
    );
    return data?.data;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const fetchACEmailActivityByStageId = async stageID => {
  try {
    const { data } = await http.get(`/events/engagement/stage/${stageID}`);
    return data?.data;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const fetchACEmailActivityByCampaignId = async campaignId => {
  try {
    const { data } = await http.get(
      `/events/engagement/campaign/${campaignId}`
    );
    return data?.data;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const createStage = async (campaignId, stageData) => {
  try {
    // Transform stage data to API format
    const transformedStage = {
      name: stageData.name,
      description: stageData.description || '',
      conditions: (stageData.conditions || []).map(condition => {
        const transformed = transformConditionToApi(condition);
        if (!transformed) {
          // Return a minimal condition if transformation fails
          return {
            name: condition?.name || null,
            delay: condition?.delay || `0|${ApiUnit.DAY}`,
          };
        }
        return transformed;
      }),
    };

    // Include template if it exists and has content
    if (isValidTemplate(stageData.template)) {
      transformedStage.template = stageData.template;
    }

    const { data } = await http.post(
      `/campaigns/${campaignId}/stages`,
      transformedStage
    );
    // Transform the returned stage from API format
    return transformCampaignFromApi({ stages: [data.data] }).stages[0];
  } catch (error) {
    handleApiError(error, { resourceName: 'campaign', resourceId: campaignId });
  }
};

export const fetchStageTemplate = async (campaignId, stageId) => {
  try {
    const { data } = await http.get(
      `/campaigns/${campaignId}/stages/${stageId}/template`
    );
    return data.data; // Return the template object directly
  } catch (error) {
    return handleApiError(error, { returnNullOn404: true });
  }
};

export const fetchStageAudience = async (campaignId, stageId) => {
  try {
    const { data } = await http.get(
      `/campaigns/${campaignId}/stages/${stageId}/audience`
    );
    return data.data; // Return the audience object directly
  } catch (error) {
    return handleApiError(error, { returnNullOn404: true });
  }
};

export const updateStageTemplate = async (campaignId, stageId, template) => {
  try {
    // First try to update via stage-specific endpoint
    const { data } = await http.put(
      `/campaigns/${campaignId}/stages/${stageId}`,
      {
        template,
      }
    );
    return data.data;
  } catch (error) {
    // If stage not found (404), fetch campaign and update via campaign endpoint
    // This handles cases where the stageId is a temporary ID that hasn't been saved yet
    if (error.response?.status === 404) {
      const errorMessage = error.response?.data?.message || error.message || '';
      const isStageNotFound =
        errorMessage === 'Stage not found' ||
        errorMessage.includes('Stage not found') ||
        errorMessage.includes('stage not found');

      if (isStageNotFound) {
        // Fetch the campaign to get current stages
        const campaign = await fetchCampaign(campaignId);

        // Find the stage in the campaign's stages
        const stageIndex = campaign.stages.findIndex(
          stage => stage._id === stageId
        );

        if (stageIndex === -1) {
          // Stage doesn't exist in database - create it with the template
          const newStage = {
            _id: stageId,
            name: `Stage ${(campaign.stages?.length || 0) + 1}`,
            template,
            conditions: [
              {
                _id: generateObjectId(),
                name: null,
                delay: { value: 0, order: 'after', unit: 'day' },
              },
            ],
          };

          const updatedStages = [...(campaign.stages || []), newStage];
          await updateCampaign(campaignId, { stages: updatedStages });
          return newStage;
        } else {
          // Stage exists in campaign but backend couldn't find it - update via campaign endpoint
          const updatedStages = campaign.stages.map((stage, index) => {
            if (index === stageIndex) {
              return {
                ...stage,
                template,
              };
            }
            return stage;
          });

          await updateCampaign(campaignId, { stages: updatedStages });
          return updatedStages[stageIndex];
        }
      }
    }
    // Re-throw other errors
    throw error;
  }
};
